/**
 * Admin Alerts Dashboard
 *
 * Platform-wide alert visibility and monitoring for Admin role.
 * Provides visibility into alerts across Service Groups, Clinical Supervisors, and Therapists
 * to identify alerts "falling through the cracks."
 *
 * @component
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  Tab,
  Tabs,
  CircularProgress,
  Alert as MuiAlert,
  IconButton,
  Collapse
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { format, formatDistanceToNow } from 'date-fns';
import adminAlertApi from '../../services/adminAlertApi';
import AlertDetailModal from './AlertDetailModal';

const PRIORITY_COLORS = {
  critical: '#d32f2f',
  urgent: '#f57c00',
  high: '#ffa726',
  medium: '#42a5f5',
  low: '#66bb6a'
};

const PRIORITY_LABELS = {
  critical: 'Critical',
  urgent: 'Urgent',
  high: 'High',
  medium: 'Medium',
  low: 'Low'
};

const STATUS_COLORS = {
  pending: '#ff9800',
  acknowledged: '#2196f3',
  resolved: '#4caf50',
  dismissed: '#9e9e9e'
};

const AlertsDashboard = () => {
  // State management
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Alerts data
  const [alerts, setAlerts] = useState([]);
  const [total, setTotal] = useState(0);
  const [statistics, setStatistics] = useState(null);
  const [slaStatus, setSlaStatus] = useState(null);
  const [serviceGroupData, setServiceGroupData] = useState([]);
  const [therapistData, setTherapistData] = useState([]);

  // Filters
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    crisisType: '',
    source: '',
    serviceGroupId: '',
    therapistId: '',
    slaStatus: '',
    dateFrom: '',
    dateTo: '',
    limit: 50,
    offset: 0,
    sortBy: 'createdAt',
    sortOrder: -1
  });

  // UI state
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);

  // Load data on mount and when filters/tab change
  useEffect(() => {
    loadData();
  }, [activeTab, filters]);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load different data based on active tab
      switch (activeTab) {
        case 0: // Overview
          await Promise.all([
            loadAlerts(),
            loadStatistics(),
            loadSLAStatus()
          ]);
          break;
        case 1: // By Service Group
          await loadServiceGroupData();
          break;
        case 2: // By Therapist
          await loadTherapistData();
          break;
        case 3: // Trends
          // Trends loading will be handled by chart component
          break;
        default:
          break;
      }
    } catch (err) {
      console.error('[AlertsDashboard] Error loading data:', err);
      setError(err.message || 'Failed to load alert data');
    } finally {
      setLoading(false);
    }
  };

  const loadAlerts = async () => {
    try {
      const data = await adminAlertApi.getAlerts(filters);
      setAlerts(data?.alerts || []);
      setTotal(data?.total || 0);
    } catch (err) {
      console.error('[AlertsDashboard] Error loading alerts:', err);
      setAlerts([]);
      setTotal(0);
      throw err;
    }
  };

  const loadStatistics = async () => {
    try {
      const data = await adminAlertApi.getAlertStatistics({
        groupBy: 'priority'
      });
      setStatistics(data || null);
    } catch (err) {
      console.error('[AlertsDashboard] Error loading statistics:', err);
      setStatistics(null);
      throw err;
    }
  };

  const loadSLAStatus = async () => {
    try {
      const data = await adminAlertApi.getSLAStatus(15);
      setSlaStatus(data || null);
    } catch (err) {
      console.error('[AlertsDashboard] Error loading SLA status:', err);
      setSlaStatus(null);
      throw err;
    }
  };

  const loadServiceGroupData = async () => {
    try {
      const data = await adminAlertApi.getAlertsByServiceGroup();
      setServiceGroupData(data?.serviceGroups || []);
    } catch (err) {
      console.error('[AlertsDashboard] Error loading service groups:', err);
      setServiceGroupData([]);
      throw err;
    }
  };

  const loadTherapistData = async () => {
    try {
      const data = await adminAlertApi.getAlertsByTherapist();
      setTherapistData(data?.therapists || []);
    } catch (err) {
      console.error('[AlertsDashboard] Error loading therapist data:', err);
      setTherapistData([]);
      throw err;
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      offset: 0 // Reset to first page on filter change
    }));
  };

  const handleSort = (field) => {
    setFilters(prev => ({
      ...prev,
      sortBy: field,
      sortOrder: prev.sortBy === field ? -prev.sortOrder : -1
    }));
  };

  const handleRefresh = () => {
    loadData();
  };

  const handleAlertClick = async (alert) => {
    setSelectedAlert(alert);
    setDetailModalOpen(true);
  };

  const getPriorityChip = (priority) => (
    <Chip
      label={PRIORITY_LABELS[priority] || priority}
      size="small"
      style={{
        backgroundColor: PRIORITY_COLORS[priority] || '#9e9e9e',
        color: 'white',
        fontWeight: 'bold'
      }}
    />
  );

  const getStatusChip = (status) => (
    <Chip
      label={status.charAt(0).toUpperCase() + status.slice(1)}
      size="small"
      style={{
        backgroundColor: STATUS_COLORS[status] || '#9e9e9e',
        color: 'white'
      }}
    />
  );

  const getSLAIndicator = (alert) => {
    if (!alert.sla || !alert.sla.responseDeadline) return null;

    const now = new Date();
    const deadline = new Date(alert.sla.responseDeadline);
    const isBreached = deadline < now && ['pending', 'acknowledged'].includes(alert.status);
    const isWarning = deadline > now && deadline < new Date(now.getTime() + 15 * 60 * 1000);

    if (isBreached) {
      return <ErrorIcon color="error" fontSize="small" />;
    }
    if (isWarning) {
      return <WarningIcon color="warning" fontSize="small" />;
    }
    return <CheckCircleIcon color="success" fontSize="small" />;
  };

  // Render statistics cards
  const renderStatisticsCards = () => {
    if (!statistics || !statistics.summary) return null;

    const { summary, slaStatus: sla } = { summary: statistics.summary, slaStatus };

    return (
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="caption">
                Total Active
              </Typography>
              <Typography variant="h4">
                {(summary.pendingAlerts || 0) + (summary.acknowledgedAlerts || 0)}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {summary.pendingAlerts || 0} pending, {summary.acknowledgedAlerts || 0} ack'd
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="caption">
                SLA Breached
              </Typography>
              <Typography variant="h4" color="error">
                {sla?.summary?.breached || 0}
              </Typography>
              <Typography variant="caption" color="warning.main">
                {sla?.summary?.approaching || 0} approaching
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card style={{ backgroundColor: PRIORITY_COLORS.critical, color: 'white' }}>
            <CardContent>
              <Typography gutterBottom variant="caption" style={{ color: 'rgba(255,255,255,0.8)' }}>
                Critical
              </Typography>
              <Typography variant="h4">
                {summary.criticalAlerts || 0}
              </Typography>
              <Typography variant="caption" style={{ color: 'rgba(255,255,255,0.8)' }}>
                15 min SLA
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card style={{ backgroundColor: PRIORITY_COLORS.urgent, color: 'white' }}>
            <CardContent>
              <Typography gutterBottom variant="caption" style={{ color: 'rgba(255,255,255,0.8)' }}>
                Urgent
              </Typography>
              <Typography variant="h4">
                {summary.urgentAlerts || 0}
              </Typography>
              <Typography variant="caption" style={{ color: 'rgba(255,255,255,0.8)' }}>
                1 hour SLA
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="caption">
                Avg Response
              </Typography>
              <Typography variant="h5">
                {summary.avgTimeToAcknowledge
                  ? `${Math.round(summary.avgTimeToAcknowledge)} min`
                  : 'N/A'}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                time to acknowledge
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="caption">
                Resolved
              </Typography>
              <Typography variant="h4" color="success.main">
                {summary.resolvedAlerts || 0}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                total resolved
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  // Render filters
  const renderFilters = () => (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              label="Status"
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="acknowledged">Acknowledged</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
              <MenuItem value="dismissed">Dismissed</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Priority</InputLabel>
            <Select
              value={filters.priority}
              label="Priority"
              onChange={(e) => handleFilterChange('priority', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="critical">Critical</MenuItem>
              <MenuItem value="urgent">Urgent</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="low">Low</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>SLA Status</InputLabel>
            <Select
              value={filters.slaStatus}
              label="SLA Status"
              onChange={(e) => handleFilterChange('slaStatus', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="breached">Breached</MenuItem>
              <MenuItem value="warning">Warning</MenuItem>
              <MenuItem value="on_track">On Track</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            fullWidth
          >
            Refresh
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );

  // Render overview tab (alert list)
  const renderOverviewTab = () => (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell>
              <TableSortLabel
                active={filters.sortBy === 'priority'}
                direction={filters.sortOrder === 1 ? 'asc' : 'desc'}
                onClick={() => handleSort('priority')}
              >
                Priority
              </TableSortLabel>
            </TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Client</TableCell>
            <TableCell>Therapist</TableCell>
            <TableCell>Service Group</TableCell>
            <TableCell>
              <TableSortLabel
                active={filters.sortBy === 'createdAt'}
                direction={filters.sortOrder === 1 ? 'asc' : 'desc'}
                onClick={() => handleSort('createdAt')}
              >
                Age
              </TableSortLabel>
            </TableCell>
            <TableCell>Status</TableCell>
            <TableCell>SLA</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {alerts && alerts.length > 0 && alerts.map((alert) => (
            <React.Fragment key={alert._id}>
              <TableRow
                hover
                onClick={() => setExpandedRow(expandedRow === alert._id ? null : alert._id)}
                style={{ cursor: 'pointer' }}
              >
                <TableCell>
                  <IconButton size="small">
                    {expandedRow === alert._id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </TableCell>
                <TableCell>{getPriorityChip(alert.priority)}</TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {alert.metadata?.crisisType?.replace(/_/g, ' ')}
                  </Typography>
                </TableCell>
                <TableCell>{alert.clientId?.name || 'N/A'}</TableCell>
                <TableCell>{alert.therapistId?.name || 'Unassigned'}</TableCell>
                <TableCell>{alert.serviceGroupId?.name || 'N/A'}</TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {formatDistanceToNow(new Date(alert.createdAt), { addSuffix: true })}
                  </Typography>
                </TableCell>
                <TableCell>{getStatusChip(alert.status)}</TableCell>
                <TableCell>{getSLAIndicator(alert)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={9} style={{ paddingBottom: 0, paddingTop: 0 }}>
                  <Collapse in={expandedRow === alert._id} timeout="auto" unmountOnExit>
                    <Box sx={{ margin: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Alert Details
                      </Typography>
                      <Typography variant="body2">
                        <strong>Explanation:</strong> {alert.concern?.explanation || 'N/A'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Suggested Action:</strong> {alert.concern?.suggestedAction || 'N/A'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Source:</strong> {alert.metadata?.source}
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleAlertClick(alert)}
                        sx={{ mt: 1 }}
                      >
                        View Full Details
                      </Button>
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            </React.Fragment>
          ))}
        </TableBody>
      </Table>

      {alerts.length === 0 && !loading && (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="textSecondary">
            No alerts found matching the current filters
          </Typography>
        </Box>
      )}
    </TableContainer>
  );

  // Render service group tab
  const renderServiceGroupTab = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Service Group</TableCell>
            <TableCell align="right">Total Alerts</TableCell>
            <TableCell align="right">Critical</TableCell>
            <TableCell align="right">Urgent</TableCell>
            <TableCell align="right">Oldest Alert</TableCell>
            <TableCell align="right">Avg Age (min)</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {serviceGroupData && serviceGroupData.length > 0 && serviceGroupData.map((group) => (
            <TableRow key={group._id} hover>
              <TableCell>
                <Typography variant="body2" fontWeight="bold">
                  {group.name}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {group.slug}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="h6">{group.totalAlerts}</Typography>
              </TableCell>
              <TableCell align="right">
                <Chip
                  label={group.criticalCount}
                  size="small"
                  style={{ backgroundColor: PRIORITY_COLORS.critical, color: 'white' }}
                />
              </TableCell>
              <TableCell align="right">
                <Chip
                  label={group.urgentCount}
                  size="small"
                  style={{ backgroundColor: PRIORITY_COLORS.urgent, color: 'white' }}
                />
              </TableCell>
              <TableCell align="right">
                <Typography variant="caption">
                  {group.oldestAlert
                    ? formatDistanceToNow(new Date(group.oldestAlert), { addSuffix: true })
                    : 'N/A'}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2">
                  {group.averageAgeMinutes ? `${group.averageAgeMinutes.toFixed(0)} min` : 'N/A'}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // Render therapist tab
  const renderTherapistTab = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Therapist</TableCell>
            <TableCell align="right">Active Alerts</TableCell>
            <TableCell align="right">Critical</TableCell>
            <TableCell align="right">Urgent</TableCell>
            <TableCell align="right">Resolved</TableCell>
            <TableCell align="right">Avg Response (min)</TableCell>
            <TableCell align="right">Avg Resolution (min)</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {therapistData && therapistData.length > 0 && therapistData.map((therapist) => (
            <TableRow key={therapist._id} hover>
              <TableCell>
                <Typography variant="body2" fontWeight="bold">
                  {therapist.name}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {therapist.email}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="h6">{therapist.activeAlerts}</Typography>
              </TableCell>
              <TableCell align="right">
                <Chip
                  label={therapist.criticalCount}
                  size="small"
                  style={{ backgroundColor: PRIORITY_COLORS.critical, color: 'white' }}
                />
              </TableCell>
              <TableCell align="right">
                <Chip
                  label={therapist.urgentCount}
                  size="small"
                  style={{ backgroundColor: PRIORITY_COLORS.urgent, color: 'white' }}
                />
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2">{therapist.resolvedAlerts}</Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2">
                  {therapist.avgTimeToAcknowledge ? `${therapist.avgTimeToAcknowledge.toFixed(0)} min` : 'N/A'}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2">
                  {therapist.avgTimeToResolve ? `${therapist.avgTimeToResolve.toFixed(0)} min` : 'N/A'}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // Main render
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Platform Alert Management
        </Typography>
      </Box>

      {error && (
        <MuiAlert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </MuiAlert>
      )}

      {renderStatisticsCards()}
      {renderFilters()}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)}>
          <Tab label="Overview" />
          <Tab label="By Service Group" />
          <Tab label="By Therapist" />
          <Tab label="Trends" />
        </Tabs>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {activeTab === 0 && renderOverviewTab()}
          {activeTab === 1 && renderServiceGroupTab()}
          {activeTab === 2 && renderTherapistTab()}
          {activeTab === 3 && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="textSecondary">
                Trends visualization coming soon
              </Typography>
            </Paper>
          )}
        </>
      )}

      {selectedAlert && (
        <AlertDetailModal
          open={detailModalOpen}
          onClose={() => {
            setDetailModalOpen(false);
            setSelectedAlert(null);
          }}
          alertId={selectedAlert._id}
        />
      )}
    </Box>
  );
};

export default AlertsDashboard;
